import React from "react";
import {
  buildFrontendPreviewUrl,
  FRONTEND_PREVIEW_BASE_URL,
  PREVIEW_IFRAME_SANDBOX
} from "../config/previewRuntime";

const STATUS_STYLES = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-rose-200 bg-rose-50 text-rose-700",
  skipped: "border-slate-200 bg-slate-50 text-slate-600"
};

function normalizeStatus(scenario) {
  if (scenario?.status) return scenario.status;
  if (scenario?.executionError) return "failed";
  if (scenario?.html) return "success";
  return "skipped";
}

const Badge = ({ children, className = "" }) => (
  <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${className}`}>
    {children}
  </span>
);

const SnapshotFallback = ({ scenario }) => {
  if (scenario.executionError) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Snapshot-fallback kunne ikke bygges for scenarioet.
        </div>
        <pre className="overflow-auto rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          {scenario.executionError}
        </pre>
      </div>
    );
  }

  if (!scenario.html) {
    return (
      <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        Ingen snapshot-fallback er tilgjengelig for scenarioet.
      </div>
    );
  }

  return (
    <div
      className="pm-preview-runtime-html rounded-md border border-slate-200 bg-white p-3"
      dangerouslySetInnerHTML={{ __html: scenario.html }}
    />
  );
};

const PreviewRenderer = ({ componentId, scenario }) => {
  const scenarioStatus = normalizeStatus(scenario);
  const previewUrl = buildFrontendPreviewUrl({
    componentId,
    scenarioId: scenario?.id
  });
  const runtimeMode = previewUrl ? "live iframe" : "snapshot HTML";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-3">
        <Badge className={STATUS_STYLES[scenarioStatus] ?? STATUS_STYLES.skipped}>
          Status: {scenarioStatus}
        </Badge>
        <Badge className="border-indigo-200 bg-indigo-50 text-indigo-700">
          Runtime: {runtimeMode}
        </Badge>
      </div>

      {previewUrl ? (
        <div className="space-y-2">
          <iframe
            key={`${componentId}:${scenario.id}`}
            title={`${componentId} / ${scenario.title || scenario.id}`}
            src={previewUrl}
            sandbox={PREVIEW_IFRAME_SANDBOX}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="h-[360px] w-full rounded-md border border-slate-200 bg-white"
          />
          <p className="text-xs text-slate-500">
            Live preview kjøres i sandboxet iframe fra{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5">
              {FRONTEND_PREVIEW_BASE_URL}
            </code>
            . Snapshot er beholdt som fallback under.
          </p>
          <details className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-slate-700">
              Snapshot-fallback
            </summary>
            <div className="mt-3">
              <SnapshotFallback scenario={scenario} />
            </div>
          </details>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
            Live preview-host er ikke konfigurert. Portalen viser snapshot-fallback.
            Sett <code>REACT_APP_FRONTEND_PREVIEW_BASE_URL</code> til for eksempel{" "}
            <code>http://localhost:3001/component-preview.html</code> for interaktiv iframe-preview.
          </div>
          <SnapshotFallback scenario={scenario} />
        </div>
      )}
    </div>
  );
};

export default PreviewRenderer;
